import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, Trash2, FileText } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import type {
  OneOffInvoiceLine,
  OneOffInvoiceRequest,
  OneOffInvoiceResponse,
  ReverseChargeMode,
} from "@/types/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function formatMoney(cents: number, currency: string): string {
  return `${(cents / 100).toFixed(2)} ${currency}`;
}

interface LineRow {
  description: string;
  unit: string;
  amountMajor: string;
  quantity: string;
  vatPercent: string;
}

const REVERSE_CHARGE_NOTE: Record<Exclude<ReverseChargeMode, "none">, string> = {
  de_13b: "Steuerschuldnerschaft des Leistungsempfängers (§13b UStG).",
  eu_b2b:
    "Reverse charge — VAT to be accounted for by the recipient (Art. 196 Directive 2006/112/EC).",
};

/**
 * Ad-hoc, line-item invoice charged outside the recurring subscription.
 * Supports a unit label, per-line VAT, net/gross entry, reverse charge, a
 * customer VAT id, service-period / PO fields, discount lines, and a
 * draft-by-default flow. On a `send_invoice` result the hosted payment link is
 * surfaced for the operator to forward to the client.
 */
export function OneOffInvoiceCard({
  name,
  vatRatePercent,
  currency,
}: {
  name: string;
  /** VAT rate as a percentage (e.g. 19), from the billing-config form state. */
  vatRatePercent: number;
  /** ISO currency code from the billing-config form state (e.g. "EUR"). */
  currency: string;
}) {
  const { t } = useTranslation("billing");
  const qc = useQueryClient();

  const defaultVat = React.useMemo(() => {
    const opts = [19, 7, 0];
    return opts.includes(vatRatePercent) ? String(vatRatePercent) : "19";
  }, [vatRatePercent]);

  const vatOptions = React.useMemo(() => {
    const set = new Set<string>(["19", "7", "0", defaultVat]);
    return [...set].sort((a, b) => Number(b) - Number(a));
  }, [defaultVat]);

  const emptyRow = React.useCallback(
    (): LineRow => ({
      description: "",
      unit: "",
      amountMajor: "",
      quantity: "1",
      vatPercent: defaultVat,
    }),
    [defaultVat]
  );

  const [rows, setRows] = React.useState<LineRow[]>([emptyRow()]);
  const [taxInclusive, setTaxInclusive] = React.useState(false);
  const [reverseCharge, setReverseCharge] = React.useState<ReverseChargeMode>("none");
  const [customerVatId, setCustomerVatId] = React.useState("");
  const [servicePeriod, setServicePeriod] = React.useState("");
  const [poNumber, setPoNumber] = React.useState("");
  const [note, setNote] = React.useState("");
  const [dueDays, setDueDays] = React.useState("14");
  const [finalize, setFinalize] = React.useState(false);
  const [sent, setSent] = React.useState<OneOffInvoiceResponse | null>(null);
  // One idempotency key per invoice being issued: resubmitting the same form
  // after a timeout / network error replays the first request in Stripe
  // instead of creating a second invoice. A new key is taken after success,
  // after a definite server answer (the server already cleaned up), or when
  // the form content changed.
  const attemptRef = React.useRef<{ body: string; key: string } | null>(null);

  const issueMutation = useMutation({
    mutationFn: (body: OneOffInvoiceRequest) =>
      api.post<OneOffInvoiceResponse>(
        `/api/admin/instances/${name}/billing/one-off-invoice`,
        body
      ),
    onSuccess: (res) => {
      attemptRef.current = null;
      setSent(res);
      if (!res.finalized) {
        toast.success(t("oneOffInvoice.toastDraft", { invoiceId: res.invoiceId }));
      } else if (res.collectionMethod === "send_invoice") {
        toast.success(t("oneOffInvoice.toastSendLink", { invoiceId: res.invoiceId }));
      } else {
        toast.success(t("oneOffInvoice.toastAutoCharge", { invoiceId: res.invoiceId }));
      }
      setRows([emptyRow()]);
      setCustomerVatId("");
      setServicePeriod("");
      setPoNumber("");
      setNote("");
      qc.invalidateQueries({ queryKey: ["instance", name, "payments"] });
      qc.invalidateQueries({ queryKey: ["instance", name, "billing-audit"] });
    },
    onError: (err: unknown) => {
      // Keep the key only when the outcome is unknown: no answer at all, or a
      // gateway error while the server may still be working on it.
      const outcomeUnknown =
        !(err instanceof ApiError) || err.status === 502 || err.status === 504;
      if (!outcomeUnknown) attemptRef.current = null;
      if (err instanceof ApiError && err.status === 503) {
        toast.error(t("stripeNotConfigured"));
      } else {
        toast.error(err instanceof Error ? err.message : t("oneOffInvoice.failed"));
      }
    },
  });

  function updateRow(index: number, patch: Partial<LineRow>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((prev) => [...prev, emptyRow()]);
  }
  function removeRow(index: number) {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  }

  // Signed per-line total in minor units (net-or-gross per taxInclusive).
  function lineCents(row: LineRow): number {
    const amt = Number(row.amountMajor);
    const qty = row.quantity.trim() ? Number(row.quantity) : 1;
    if (!Number.isFinite(amt) || amt === 0) return 0;
    if (!Number.isInteger(qty) || qty < 1) return 0;
    return Math.round(amt * 100) * qty;
  }

  // VAT summary grouped by rate (bps). Discounts (negative) net out.
  const summary = React.useMemo(() => {
    const groups = new Map<number, { net: number; vat: number }>();
    for (const row of rows) {
      const cents = lineCents(row);
      if (!cents) continue;
      const rateBps =
        reverseCharge !== "none" ? 0 : Math.round(Number(row.vatPercent || "0") * 100);
      let net: number;
      let vat: number;
      if (rateBps === 0) {
        net = cents;
        vat = 0;
      } else if (taxInclusive) {
        net = Math.round(cents / (1 + rateBps / 10000));
        vat = cents - net;
      } else {
        net = cents;
        vat = Math.round((cents * rateBps) / 10000);
      }
      const g = groups.get(rateBps) ?? { net: 0, vat: 0 };
      g.net += net;
      g.vat += vat;
      groups.set(rateBps, g);
    }
    const rows2 = [...groups.entries()]
      .map(([rateBps, v]) => ({ rateBps, ...v }))
      .sort((a, b) => b.rateBps - a.rateBps);
    const net = rows2.reduce((s, g) => s + g.net, 0);
    const vat = rows2.reduce((s, g) => s + g.vat, 0);
    return { groups: rows2, net, vat, total: net + vat };
  }, [rows, reverseCharge, taxInclusive]);

  function handleIssue() {
    const lines: OneOffInvoiceLine[] = [];
    for (const r of rows) {
      const description = r.description.trim();
      const amount = Number(r.amountMajor);
      const qty = r.quantity.trim() ? Number(r.quantity) : 1;
      if (!description) {
        toast.error(t("oneOffInvoice.errorDescription"));
        return;
      }
      if (!Number.isFinite(amount) || amount === 0) {
        toast.error(t("oneOffInvoice.errorAmount"));
        return;
      }
      if (!Number.isInteger(qty) || qty < 1) {
        toast.error(t("oneOffInvoice.errorQuantity"));
        return;
      }
      const line: OneOffInvoiceLine = {
        description,
        amountCents: Math.round(amount * 100),
      };
      if (r.unit.trim()) line.unit = r.unit.trim();
      if (qty !== 1) line.quantity = qty;
      if (reverseCharge === "none") line.vatRateBps = Math.round(Number(r.vatPercent || "0") * 100);
      lines.push(line);
    }
    if (lines.length < 1 || lines.length > 50) {
      toast.error(t("oneOffInvoice.errorLineCount"));
      return;
    }
    if (reverseCharge !== "none" && !customerVatId.trim()) {
      toast.error(t("oneOffInvoice.errorVatId"));
      return;
    }
    const due = Number(dueDays);
    if (!Number.isInteger(due) || due < 1 || due > 60) {
      toast.error(t("oneOffInvoice.errorDueDays"));
      return;
    }

    const body: OneOffInvoiceRequest = {
      lines,
      taxInclusive,
      dueDays: due,
      finalize,
    };
    if (reverseCharge !== "none") body.reverseCharge = reverseCharge;
    if (customerVatId.trim()) body.customerVatId = customerVatId.trim();
    if (servicePeriod.trim()) body.servicePeriod = servicePeriod.trim();
    if (poNumber.trim()) body.poNumber = poNumber.trim();
    if (note.trim()) body.note = note.trim();

    const bodyKey = JSON.stringify(body);
    if (attemptRef.current?.body !== bodyKey) {
      attemptRef.current = { body: bodyKey, key: crypto.randomUUID() };
    }
    body.idemKey = attemptRef.current.key;

    setSent(null);
    issueMutation.mutate(body);
  }

  const amountsAre = taxInclusive ? t("oneOffInvoice.amountsGross") : t("oneOffInvoice.amountsNet");

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("oneOffInvoice.title")}</CardTitle>
        <CardDescription>
          {t("oneOffInvoice.description", { currency, mode: amountsAre })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {rows.map((row, i) => (
            <div
              key={i}
              className="grid items-end gap-2 sm:grid-cols-[1fr_5rem_7rem_4rem_6rem_auto]"
            >
              <div className="space-y-1.5">
                {i === 0 ? <Label htmlFor={`bo-line-desc-${i}`}>{t("oneOffInvoice.lineDescription")}</Label> : null}
                <Input
                  id={`bo-line-desc-${i}`}
                  value={row.description}
                  onChange={(e) => updateRow(i, { description: e.target.value })}
                  placeholder={t("oneOffInvoice.lineDescriptionPlaceholder")}
                />
              </div>
              <div className="space-y-1.5">
                {i === 0 ? <Label htmlFor={`bo-line-unit-${i}`}>{t("oneOffInvoice.unit")}</Label> : null}
                <Input
                  id={`bo-line-unit-${i}`}
                  value={row.unit}
                  onChange={(e) => updateRow(i, { unit: e.target.value })}
                  placeholder={t("oneOffInvoice.unitPlaceholder")}
                />
              </div>
              <div className="space-y-1.5">
                {i === 0 ? <Label htmlFor={`bo-line-amount-${i}`}>{t("oneOffInvoice.unitPrice")}</Label> : null}
                <Input
                  id={`bo-line-amount-${i}`}
                  inputMode="decimal"
                  value={row.amountMajor}
                  onChange={(e) => updateRow(i, { amountMajor: e.target.value })}
                  placeholder="100.00"
                />
              </div>
              <div className="space-y-1.5">
                {i === 0 ? <Label htmlFor={`bo-line-qty-${i}`}>{t("oneOffInvoice.qty")}</Label> : null}
                <Input
                  id={`bo-line-qty-${i}`}
                  inputMode="numeric"
                  value={row.quantity}
                  onChange={(e) => updateRow(i, { quantity: e.target.value })}
                  placeholder="1"
                />
              </div>
              <div className="space-y-1.5">
                {i === 0 ? <Label>{t("oneOffInvoice.vat")}</Label> : null}
                {reverseCharge !== "none" ? (
                  <div className="flex h-9 items-center rounded-md border border-input bg-muted px-2 text-xs text-muted-foreground">
                    {t("oneOffInvoice.reverseChargeShort")}
                  </div>
                ) : (
                  <Select
                    value={row.vatPercent}
                    onValueChange={(v) => updateRow(i, { vatPercent: v })}
                  >
                    <SelectTrigger aria-label={t("oneOffInvoice.vatRateAria", { line: i + 1 })}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {vatOptions.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}%
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeRow(i)}
                disabled={rows.length <= 1}
                aria-label={t("oneOffInvoice.removeLineAria", { line: i + 1 })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <Button variant="outline" size="sm" onClick={addRow}>
            <Plus className="mr-2 h-4 w-4" /> {t("oneOffInvoice.addLine")}
          </Button>
          <div className="min-w-[12rem] text-right text-sm">
            <div className="text-muted-foreground">
              {t("oneOffInvoice.netSubtotal")}{" "}
              <span className="tabular-nums">{formatMoney(summary.net, currency)}</span>
            </div>
            {reverseCharge !== "none" ? (
              <div className="text-muted-foreground">{t("oneOffInvoice.reverseChargeVat")}</div>
            ) : (
              summary.groups
                .filter((g) => g.rateBps > 0)
                .map((g) => (
                  <div key={g.rateBps} className="text-muted-foreground">
                    {t("oneOffInvoice.vatGroup", { rate: g.rateBps / 100 })}{" "}
                    <span className="tabular-nums">{formatMoney(g.vat, currency)}</span>
                  </div>
                ))
            )}
            <div className="font-medium">
              {t("oneOffInvoice.total")} <span className="tabular-nums">{formatMoney(summary.total, currency)}</span>
            </div>
          </div>
        </div>

        <div className="grid gap-3 border-t pt-4 sm:grid-cols-2">
          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <div>
              <Label htmlFor="bo-inclusive">{t("oneOffInvoice.taxInclusive")}</Label>
              <p className="text-xs text-muted-foreground">{t("oneOffInvoice.taxInclusiveHint")}</p>
            </div>
            <Switch id="bo-inclusive" checked={taxInclusive} onCheckedChange={setTaxInclusive} />
          </div>

          <div className="space-y-1.5">
            <Label>{t("oneOffInvoice.reverseCharge")}</Label>
            <Select
              value={reverseCharge}
              onValueChange={(v) => setReverseCharge(v as ReverseChargeMode)}
            >
              <SelectTrigger aria-label={t("oneOffInvoice.reverseChargeAria")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("oneOffInvoice.reverseChargeNone")}</SelectItem>
                <SelectItem value="de_13b">{t("oneOffInvoice.reverseChargeDe13b")}</SelectItem>
                <SelectItem value="eu_b2b">{t("oneOffInvoice.reverseChargeEuB2b")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bo-vatid">{t("oneOffInvoice.customerVatId")}</Label>
            <Input
              id="bo-vatid"
              value={customerVatId}
              onChange={(e) => setCustomerVatId(e.target.value)}
              placeholder="DE123456789"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bo-due">{t("oneOffInvoice.paymentTerm")}</Label>
            <Input
              id="bo-due"
              inputMode="numeric"
              value={dueDays}
              onChange={(e) => setDueDays(e.target.value)}
              placeholder="14"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bo-period">{t("oneOffInvoice.servicePeriod")}</Label>
            <Input
              id="bo-period"
              value={servicePeriod}
              onChange={(e) => setServicePeriod(e.target.value)}
              placeholder="2026-07-01 – 2026-07-31"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bo-po">{t("oneOffInvoice.poNumber")}</Label>
            <Input
              id="bo-po"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              placeholder="PO-1042"
            />
          </div>

          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="bo-note">{t("oneOffInvoice.note")}</Label>
            <Textarea
              id="bo-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("oneOffInvoice.notePlaceholder")}
              rows={2}
            />
            {reverseCharge !== "none" ? (
              <p className="text-xs text-muted-foreground">
                {t("oneOffInvoice.footerWillInclude", { note: REVERSE_CHARGE_NOTE[reverseCharge] })}
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md border p-3 sm:col-span-2">
            <div>
              <Label htmlFor="bo-finalize">{t("oneOffInvoice.finalize")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("oneOffInvoice.finalizeHint")}
              </p>
            </div>
            <Switch id="bo-finalize" checked={finalize} onCheckedChange={setFinalize} />
          </div>
        </div>

        {sent ? (
          <div
            role="status"
            className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200"
          >
            {!sent.finalized ? (
              <p className="font-medium">
                {t("oneOffInvoice.sentDraft", { invoiceId: sent.invoiceId })}
              </p>
            ) : sent.collectionMethod === "send_invoice" ? (
              <p className="font-medium">{t("oneOffInvoice.sentNoPaymentMethod")}</p>
            ) : (
              <p className="font-medium">
                {t("oneOffInvoice.sentAutoCharge", { invoiceId: sent.invoiceId })}
              </p>
            )}
            {sent.hostedInvoiceUrl ? (
              <a
                className="block break-all text-primary hover:underline"
                href={sent.hostedInvoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {sent.hostedInvoiceUrl}
              </a>
            ) : null}
            {sent.invoicePdf ? (
              <a
                className="inline-flex items-center gap-1 text-primary hover:underline"
                href={sent.invoicePdf}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileText className="h-3.5 w-3.5" /> {t("oneOffInvoice.downloadPdf")}
              </a>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end">
          <Button onClick={handleIssue} disabled={issueMutation.isPending}>
            {issueMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {finalize ? t("oneOffInvoice.issue") : t("oneOffInvoice.createDraft")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
